import CustomAlert from "@/app/components/alerts/CustomAlert";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { SiteQuestionTotal } from "@/types/test-builder-schema";
import {
  Button,
  Divider,
  Flex,
  NumberInput,
  Stack,
  Title,
  TitleOrder,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import FormatListNumberedRounded from "@mui/icons-material/FormatListNumberedRounded";

import { useEffect, useMemo, useState } from "react";
import { convertSecondsToMinutes } from "../../../../../../../../../lib/stripe/formatters";
import {
  getTestLaMarks,
  getTestLaSeconds,
  getTestMcMarks,
  getTestMcSeconds,
  getTestTotalSeconds,
} from "../../../test-taker/components/TestMetrics";
import McLaBreakdown from "./McLaBreakdown";
import RestrictedRangeSlider from "./RestrictedRangeSlider";
import classes from "./TestFormatStep.module.css";
import { LimitType } from "./types";

interface StepFormatProps {
  subjectNameLower: string;
  limitType: LimitType;
  limitValue: number;
  mcAbsoluteProportion: number;
  numSelectedSubTopics: number;
  testBuilderQuestionLimits: {
    MC: SiteQuestionTotal;
    LA: SiteQuestionTotal;
  };
  onLimitTypeChange: (value: LimitType) => void;
  onLimitValueChange: (value: number) => void;
  onRatioChange: (value: number) => void;
}

const upperQuestionLimit = 40;
const upperTimeLimit = 100;

export default function TestFormatStep({
  subjectNameLower,
  limitType,
  limitValue,
  mcAbsoluteProportion,
  numSelectedSubTopics,
  testBuilderQuestionLimits,
  onLimitTypeChange,
  onLimitValueChange,
  onRatioChange,
}: StepFormatProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme("light");
  const isDark = colorScheme === "dark";
  const maxValueLimit = useMemo(() => {
    return limitType === "questions"
      ? getMaxQuestionsPossible(upperQuestionLimit, testBuilderQuestionLimits)
      : getMaxMinutesPossible(upperTimeLimit, testBuilderQuestionLimits);
  }, [testBuilderQuestionLimits, limitType]);

  const isCappedAtDefaultMaxLimit =
    limitType === "questions"
      ? maxValueLimit === upperQuestionLimit
      : maxValueLimit === upperTimeLimit;

  const { minMcContribution, maxMcContribution } = useMemo(() => {
    return getMcAbsoluteProportionLimits(
      limitType,
      limitValue,
      testBuilderQuestionLimits
    );
  }, [limitType, limitValue, testBuilderQuestionLimits]);

  useEffect(() => {
    if (limitValue > maxValueLimit) {
      onLimitValueChange(maxValueLimit);
    }
  }, [limitValue, maxValueLimit, onLimitValueChange]);

  useEffect(() => {
    if (mcAbsoluteProportion > maxMcContribution) {
      onRatioChange(maxMcContribution);
    } else if (mcAbsoluteProportion < minMcContribution) {
      onRatioChange(minMcContribution);
    }
  }, [
    mcAbsoluteProportion,
    maxMcContribution,
    minMcContribution,
    onRatioChange,
  ]);

  const numQuestions = useMemo(() => {
    return convertLimitValueToQuestions(
      limitType,
      limitValue,
      testBuilderQuestionLimits
    );
  }, [limitType, limitValue, testBuilderQuestionLimits]);

  useEffect(() => {
    onLimitValueChange(0);
  }, [limitType, onLimitValueChange]);

  const [showSubTopicWarning, setShowSubTopicWarning] = useState(false);

  useEffect(() => {
    const shouldShow = numQuestions > 0 && numQuestions < numSelectedSubTopics;
    const timer = setTimeout(() => {
      setShowSubTopicWarning(shouldShow);
    }, 250);

    return () => clearTimeout(timer);
  }, [numQuestions, numSelectedSubTopics]);

  const titleProps = {
    order: 5 as TitleOrder,
    c: isDark ? theme.colors.dark[0] : theme.colors.dark[4],
    mb: "1rem",
  };

  return (
    <Stack gap="2.5rem">
      {numSelectedSubTopics === 0 && (
        <CustomAlert
          message="Please select at least one subtopic to configure the test format."
          visualStyle="error"
        />
      )}
      <div>
        <Title {...titleProps}>1) Select format</Title>
        <Button.Group>
          {(
            [
              {
                value: "time" as const,
                label: "Time based",
                icon: <AccessTimeRounded fontSize="small" />,
              },
              {
                value: "questions" as const,
                label: "Question based",
                icon: <FormatListNumberedRounded fontSize="small" />,
              },
            ] as const
          ).map(({ value, label, icon }) => {
            const selected = limitType === value;
            return (
              <Button
                key={value}
                color="darkblue"
                size="sm"
                variant={selected ? "filled" : "outline"}
                onClick={() => onLimitTypeChange(value)}
                leftSection={icon}
                className={classes.formatButton}
              >
                {label}
              </Button>
            );
          })}
        </Button.Group>
      </div>
      <Divider color="gray.1" />
      <div>
        <Title {...titleProps}>
          2){" "}
          {limitType === "time"
            ? "Set test duration"
            : "Set number of questions"}
        </Title>
        <CustomAlert visualStyle="subtle">
          {showSubTopicWarning &&
            (limitType === "time"
              ? "The estimated number of questions for this duration is lower than subtopics selected. Some subtopics will not appear in the test. "
              : "The number of questions is lower than subtopics selected. Some subtopics will not appear in the test. ")}
          {limitType === "time"
            ? isCappedAtDefaultMaxLimit
              ? `Our max time limit is ${upperTimeLimit} minutes.`
              : `Based on the content selected, the max duration is ${maxValueLimit} minutes.`
            : isCappedAtDefaultMaxLimit
            ? `Our max question limit is ${upperQuestionLimit}.`
            : `Based on the content selected, the max question limit is ${maxValueLimit}.`}
        </CustomAlert>
        <NumberInput
          mt="sm"
          size="md"
          value={limitValue}
          onChange={(value) =>
            onLimitValueChange(typeof value === "number" ? value : 0)
          }
          min={1}
          max={maxValueLimit}
          aria-label={
            limitType === "time"
              ? "Enter duration in minutes"
              : "Enter number of questions"
          }
        />
      </div>
      <Divider color="gray.1" />
      <div>
        <Title {...titleProps}>3) Mix of question types</Title>
        <Flex gap="lg" className={classes.flexResponsive}>
          <McLaBreakdown
            subjectNameLower={subjectNameLower}
            mcValue={mcAbsoluteProportion}
            laValue={limitValue - mcAbsoluteProportion}
            limitType={limitType}
          />
          <div style={{ flexGrow: 1 }}>
            <RestrictedRangeSlider
              disabled={minMcContribution === maxMcContribution}
              subjectColorIndex={getSubjectColorIndex(subjectNameLower)}
              value={mcAbsoluteProportion}
              onChange={onRatioChange}
              minValid={minMcContribution}
              maxValid={maxMcContribution}
              maxValue={limitValue}
            />
            <CustomAlert
              message={getProportionDescription(
                limitValue,
                limitType,
                mcAbsoluteProportion,
                minMcContribution,
                maxMcContribution,
                testBuilderQuestionLimits
              )}
              visualStyle="subtle"
            />
          </div>
        </Flex>
      </div>
    </Stack>
  );
}

function getMaxQuestionsPossible(
  imposedMaxQuestions: number,
  testBuilderQuestionLimits: {
    MC: SiteQuestionTotal;
    LA: SiteQuestionTotal;
  }
) {
  const theoreticalMaxQ =
    testBuilderQuestionLimits.MC.numQuestions +
    testBuilderQuestionLimits.LA.numQuestions;
  return theoreticalMaxQ ? Math.min(theoreticalMaxQ, imposedMaxQuestions) : 0;
}

function getMaxMinutesPossible(
  imposedMaxMarks: number,
  testBuilderQuestionLimits: {
    MC: SiteQuestionTotal;
    LA: SiteQuestionTotal;
  }
) {
  const theoreticalMaxS = getTestTotalSeconds(
    testBuilderQuestionLimits.MC.numMarks,
    testBuilderQuestionLimits.LA.numMarks
  );
  const imposedMaxS = getTestTotalSeconds(
    imposedMaxMarks / 2,
    imposedMaxMarks / 2
  );
  return convertSecondsToMinutes(Math.min(theoreticalMaxS, imposedMaxS));
}

function getMcAbsoluteProportionLimits(
  limitType: LimitType,
  limitValue: number,
  testBuilderQuestionLimits: {
    MC: SiteQuestionTotal;
    LA: SiteQuestionTotal;
  }
) {
  const maxMCQuota =
    limitType === "questions"
      ? testBuilderQuestionLimits.MC.numQuestions
      : convertSecondsToMinutes(
          getTestMcSeconds(testBuilderQuestionLimits.MC.numMarks)
        );
  const maxLAQuota =
    limitType === "questions"
      ? testBuilderQuestionLimits.LA.numQuestions
      : convertSecondsToMinutes(
          getTestLaSeconds(testBuilderQuestionLimits.LA.numMarks)
        );

  const maxMcContribution = Math.min(limitValue, maxMCQuota);
  const minMcContribution = Math.max(0, limitValue - maxLAQuota);
  return { minMcContribution, maxMcContribution };
}

function convertLimitValueToQuestions(
  limitType: LimitType,
  limitValue: number,
  testBuilderQuestionLimits: {
    MC: SiteQuestionTotal;
    LA: SiteQuestionTotal;
  }
): number {
  if (limitType === "questions") {
    return limitValue;
  }
  const totalSeconds = limitValue * 60;
  const totalMarks =
    getTestMcMarks(totalSeconds / 2) + getTestLaMarks(totalSeconds / 2);
  const totalQuestions =
    testBuilderQuestionLimits.MC.numQuestions +
    testBuilderQuestionLimits.LA.numQuestions;
  const totalMarksAvailable =
    testBuilderQuestionLimits.MC.numMarks +
    testBuilderQuestionLimits.LA.numMarks;

  const avgQuestionsPerMark =
    totalMarksAvailable > 0 ? totalQuestions / totalMarksAvailable : 0;

  return Math.round(totalMarks * avgQuestionsPerMark);
}

function getProportionDescription(
  limitValue: number,
  limitType: LimitType,
  mcAbsoluteProportion: number,
  minMcContribution: number,
  maxMcContribution: number,
  testBuilderQuestionLimits: {
    MC: SiteQuestionTotal;
    LA: SiteQuestionTotal;
  }
): string {
  if (!limitValue) {
    return "Please set your test limit to configure the mix of question types.";
  }

  const unit = limitType === "questions" ? "questions" : "minutes";
  const baseMessage = "Based on the content available, your test must include ";

  if (limitType === "questions") {
    const totalQuestions =
      testBuilderQuestionLimits.MC.numQuestions +
      testBuilderQuestionLimits.LA.numQuestions;

    if (limitValue === totalQuestions) {
      return `${baseMessage}${mcAbsoluteProportion} multiple choice and ${
        limitValue - mcAbsoluteProportion
      } long answer ${unit}.`;
    }

    if (minMcContribution === maxMcContribution) {
      return `${baseMessage}${maxMcContribution} multiple choice ${unit}.`;
    }

    if (minMcContribution === 0) {
      return `${baseMessage}up to ${maxMcContribution} multiple choice ${unit}.`;
    }

    return `${baseMessage}between ${minMcContribution} and ${maxMcContribution} multiple choice ${unit}.`;
  } else {
    const totalMarks =
      testBuilderQuestionLimits.MC.numMarks +
      testBuilderQuestionLimits.LA.numMarks;

    if (limitValue === totalMarks) {
      return `${baseMessage}${mcAbsoluteProportion} ${unit} of multiple choice and ${
        limitValue - mcAbsoluteProportion
      } ${unit} of long answer questions.`;
    }

    if (minMcContribution === maxMcContribution) {
      return `${baseMessage}${maxMcContribution} ${unit} of multiple choice.`;
    }

    if (minMcContribution === 0) {
      return `${baseMessage}up to ${maxMcContribution} ${unit} of multiple choice questions.`;
    }

    return `${baseMessage}between ${minMcContribution} and ${maxMcContribution} ${unit} of multiple choice questions.`;
  }
}
